<template>
  <div class="pipeline-settings" v-if="!serversLoading">
    <div class="columns">
      <div class="column is-5">
        <edit-button :editOptions="['save']" @click="save" :disabled="saving">
          <template>
            <span class="text">{{ $t('pipeline.save') }}</span>
          </template>
        </edit-button>
        <draggable :list="configs.pipeline" class="pipelines" handle=".handle" @change="movedPipeline">
          <div v-for="(element, key) in configs.pipeline" :key="key" class="pipeline">
            <div class="header" :class="[element.type, { inactive: !element.is_active }]">
              <div
                class="name"
                @click="
                  element.expand = !element.expand
                  $forceUpdate()
                "
              >
                <i class="fas fa-grip-vertical handle"></i>
                <span class="title">
                  {{ element.displayed_name }}
                  <span v-if="element.isNew">&nbsp;(new)</span>
                </span>
              </div>
              <div class="actions">
                <i
                  @click="changeMonitoredPipeline(key)"
                  v-show="element.is_active && !element.isNew"
                  :class="{
                    'fas fa-eye': monitoredPipeline == key,
                    'fas fa-eye unmonitored': monitoredPipeline != key
                  }"
                ></i>
                <b-switch
                  v-model="element.is_active"
                  size="is-small"
                  @input="changeMonitoredPipeline(key, $event, true)"
                ></b-switch>
                <i class="fas fa-trash-alt" @click="removePipeline(key)"></i>
              </div>
            </div>
            <div class="body" :disabled="!element.is_active" v-show="element.expand">
              <div class="description" v-html="element.config.description"></div>
              <parametrs-list
                v-if="isRenderParametrs"
                :items.sync="element.config.parameters"
                :parameters="element.properties.parameters"
                :disabled="!element.is_active"
                @draw="draw($event, key)"
              >
              </parametrs-list>
            </div>
          </div>
        </draggable>
        <edit-button class="add-new" :position="null" :editOptions="addNewTypes" @click="addNewElement('')">
          <template #edit-actions>
            <b-dropdown-item
              aria-role="listitem"
              v-for="(value, key) in addNewTypes"
              :key="key"
              @click="addNewElement(value)"
            >
              {{ value }}
            </b-dropdown-item>
          </template>
          <template>
            <span class="text">
              {{ $t('pipeline.add') }}
            </span>
          </template>
        </edit-button>
      </div>
      <div class="column is-7">
        <actions position="is-bottom-left">
          <template>
            <b-dropdown-item @click="run(true)" aria-role="listitem">{{ $t('pipeline.fast_forward') }}</b-dropdown-item>
            <b-dropdown-item @click="toDuplicate" aria-role="listitem">{{ $t('lists.duplicate') }}</b-dropdown-item>
            <b-dropdown-item @click="saveToFile" aria-role="listitem">{{
              $t('pipeline.save_to_file')
            }}</b-dropdown-item>
            <b-dropdown-item @click="openDialogRemove" aria-role="listitem">{{
              $t('server.actions.delete')
            }}</b-dropdown-item>
          </template>
        </actions>
        <div class="pipeline-details">
          <div class="server-info">
            <div class="description">
              <div class="source-name">
                <router-link
                  class="add-new-link name"
                  :to="{
                    name: 'source',
                    params: {
                      source_id: configs.source.source_id
                    }
                  }"
                  >{{ configs.source.name }}</router-link
                >
                <span class="type">&nbsp;({{ configs.source.type }})</span>
              </div>
              <button class="button" @click="gotoStart">{{ $t('pipeline.goto_start') }}</button>
              <button class="button" @click="openChangeSourceModal">{{ $t('pipeline.change_source') }}</button>
            </div>
            <div class="actions">
              <div class="buttons">
                <button
                  class="button is-success play"
                  :class="{ active: status === 'run' }"
                  :disabled="
                    (status !== 'pause' && status !== 'stop' && status !== 'run' && status !== 'in queue') ||
                      imageLoading
                  "
                  @click="run(false)"
                >
                  <span class="icon is-small">
                    <i class="fas fa-play"></i>
                  </span>
                </button>
                <button
                  class="button forward"
                  :disabled="(status !== 'pause' && status !== 'run') || imageLoading"
                  @click="stepForward"
                >
                  <span class="icon is-small">
                    <svg style="width:36px;height:36px" viewBox="0 0 24 24">
                      <path :fill="status === 'pause' ? '#4bbd4d' : '#686868'" d="M5,5V19H8V5M10,5V19L21,12" />
                    </svg>
                  </span>
                </button>
                <button
                  class="button is-danger stop"
                  :class="{ active: status === 'stop' }"
                  :disabled="status === 'processing' || imageLoading"
                  @click="stop"
                >
                  <span class="icon is-small">
                    <i class="fas fa-stop"></i>
                  </span>
                </button>
              </div>
              <pipeline-status-indicator :status="status"></pipeline-status-indicator>
            </div>
          </div>
          <div class="video">
            <div
              class="image"
              v-show="image"
              @wheel="zoom"
              @mousemove="move"
              @mousedown="onmousedown"
              @mouseup="onmouseup"
              @dblclick="isFullHistogram = true"
            >
              <img :src="image" />
            </div>
            <div v-show="image == null" class="video-data">
              <audio id="audio" autoplay ref="audio"></audio>
              <video id="video" autoplay playsinline ref="video" controls></video>
            </div>
          </div>
          <div class="server-info widget">
            <div class="details" ref="details" :style="{ height: '300px' }">
              <json-tree v-if="!detailsLoading" :data="details" :level="1"></json-tree>
              <loader v-else></loader>
            </div>
            <i @mousedown.left="changeWidgetHeight" class="fas fa-arrows-alt-v"></i>
          </div>
          <!--          <div-->
          <!--            v-if="histogramLoading || histogram"-->
          <!--            class="histogram"-->
          <!--            @wheel="zoom"-->
          <!--            @mousemove="move"-->
          <!--            @mousedown="onmousedown"-->
          <!--            @mouseup="onmouseup"-->
          <!--            @dblclick="isFullHistogram = true"-->
          <!--          >-->
          <!--            <img v-if="!histogramLoading" :src="histogram" />-->
          <!--            <loader v-else></loader>-->
          <!--          </div>-->
          <div class="pipelines advance">
            <div class="pipeline">
              <div class="header">
                <div class="name">
                  <span class="title">{{ $t('pipeline.advanced_parameters') }}</span>
                </div>
              </div>
              <div class="body">
                <parametrs-list
                  :items.sync="configs.advanced_parameters"
                  :parameters="advanceProperties"
                  :disabled="$route.name === 'add_pipeline'"
                >
                </parametrs-list>
                <div class="save">
                  <div class="flex"></div>
                  <button @click="saveAdvanced" class="button" :disabled="$route.name === 'add_pipeline'">
                    {{ $t('pipeline.save_advanced_parameters') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <modal-layout
      :active="isModalChangeSource"
      :items="sourcesNames"
      :title="$t('pipeline.modals.changeSource')"
      :libraryName="$t('pipeline.modals.configuredSources')"
      :buttonName="$t('button.add.changeSource')"
      :isLoading="modalLoading"
      field="label"
      field_description="default_description"
      link="sources"
      linkName="configure another source"
      @click:load="changeSource($event)"
      @close="isModalChangeSource = false"
    ></modal-layout>
    <modal-layout
      :active="isModalProcessor"
      :items="availableProcessors"
      :isSearchable="true"
      :title="$t('pipeline.modals.addProcessor')"
      :buttonName="$t('button.add.addToPipeline')"
      :isLoading="modalLoading"
      field="label"
      field_description="default_description"
      @click:load="addPipeline($event, 'processor')"
      @close="isModalProcessor = false"
    ></modal-layout>
    <modal-layout
      :active="isModalModel"
      :items="modelsNames"
      :title="$t('pipeline.modals.addModel')"
      :libraryName="$t('pipeline.modals.loadedModels')"
      :buttonName="$t('button.add.addToPipeline')"
      :isLoading="modalLoading"
      field="label"
      field_description="default_description"
      @click:load="addPipeline($event, 'model')"
      @close="isModalModel = false"
    ></modal-layout>
    <modal-layout
      :active="isModalOutput"
      :items="outputsNames"
      :title="$t('pipeline.modals.addOutput')"
      :libraryName="$t('pipeline.modals.configuredOutputs')"
      :buttonName="$t('button.add.addToPipeline')"
      :isLoading="modalLoading"
      field="label"
      field_description="default_description"
      @click:load="addPipeline($event, 'output')"
      @close="isModalOutput = false"
    ></modal-layout>
    <b-modal :active.sync="isDrawingModalActive" has-modal-card @close="closeDrawingModal" :width="`76%`" class="modal">
      <div v-if="!imageLoading" @reset="resize" class="visual-drawing">
        <img class="visual-sample" ref="visual-sample-modal" :src="image" />
        <canvas-drawing
          :active.sync="active"
          :shape="shape"
          :width="widthModal"
          :height="heightModal"
          :coordinatesInputs="coordinates"
          @edit="editData"
          @data="getData"
          :is-modal="true"
          :key="isDrawingModalActive"
        ></canvas-drawing>
      </div>
      <loader v-else></loader>
      <div v-show="!imageLoading" class="field is-grouped is-buttons-under-canvas">
        <p class="control">
          <a class="button" @click="drawShape(shape)">{{ $t(`button.draw.${shape}`) }}</a>
        </p>
      </div>
    </b-modal>
    <b-modal :active.sync="isFullImage" has-modal-card :width="`76%`" class="modal">
      <div
        class="visual-drawing zoomable"
        @wheel="zoom"
        @mousemove="move"
        @mousedown="onmousedown"
        @mouseup="onmouseup"
      >
        <img class="visual-sample" ref="visual-sample-modal" :src="image" />
      </div>
    </b-modal>
    <!--    <b-modal :active.sync="isFullHistogram" has-modal-card :width="`76%`" class="modal">-->
    <!--      <div-->
    <!--        class="visual-drawing zoomable"-->
    <!--        @wheel="zoom"-->
    <!--        @mousemove="move"-->
    <!--        @mousedown="onmousedown"-->
    <!--        @mouseup="onmouseup"-->
    <!--      >-->
    <!--        <img class="visual-sample" ref="visual-sample-modal" :src="histogram" />-->
    <!--      </div>-->
    <!--    </b-modal>-->
  </div>
  <loader v-else />
</template>

<script>
import { createNamespacedHelpers, mapActions } from 'vuex'
import EditButton from '@/components/EditButton'
import ModalLayout from '@/layout/ModalLayout'
import Actions from '@/components/Actions'
import Loader from '@/components/Loader'
import ParametrsList from '@/components/ParametrsList'
import draggable from 'vuedraggable'
import CanvasDrawing from '@/components/CanvasDrawing'
import PipelineStatusIndicator from '@/components/PipelineStatusIndicator'
import JsonTree from 'vue-json-tree'
const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'Pipeline',
  components: {
    draggable,
    ModalLayout,
    EditButton,
    Actions,
    ParametrsList,
    Loader,
    CanvasDrawing,
    PipelineStatusIndicator,
    JsonTree
  },
  computed: {
    ...mapUserState({
      role: state => state.user.role,
      title: state => state.title
    }),
    isAdmin() {
      return this.role === 'ADMIN'
    }
  },
  data() {
    return {
      status: 'processing',
      configs: {},
      serversLoading: false,
      saving: false,
      openSample: true,
      modalLoading: false,
      detailsLoading: false,
      histogramLoading: false,
      imageLoading: false,
      isModalChangeSource: false,
      isModalProcessor: false,
      isModalModel: false,
      isModalOutput: false,
      isFullImage: false,
      isFullHistogram: false,
      sourcesNames: [],
      availableProcessors: [],
      modelsNames: [],
      outputsNames: [],
      addNewTypes: ['processor', 'model', 'output'],
      processorProperties: {},
      outputProperties: {},
      modelProperties: {},
      monitoredPipeline: 0,
      advanceProperties: {},
      details: '',
      mousePosition: null,
      histogram: null,
      image: null,
      peerConnection: null,
      coordinates: [
        {
          x: 0,
          y: 0
        },
        {
          x: 0,
          y: 0
        }
      ],
      active: false,
      shape: 'rectangle',
      widthModal: null,
      heightModal: null,
      isDrawingModalActive: false,
      updateDrawItem: [],
      isRemovePipelineClickedAt: 0,
      mouseDown: false,
      isRenderParametrs: true
    }
  },
  created() {
    const vm = this
    document.body.onmousedown = () => {
      vm.mouseDown = true
      vm.$forceUpdate()
    }
    document.body.onmouseup = () => {
      vm.mouseDown = false
      vm.$forceUpdate()
    }
    if (!this.isAdmin) {
      this.$router.push({
        name: 'pipelines',
        params: {
          server_id: this.$route.params.server_id
        }
      })
    }
    this.getDefaultConfig()
  },
  methods: {
    ...mapActions('general', ['setTitle']),
    ...mapActions('general', ['setServerName']),
    refetchStatus() {
      const body = {
        server_id: this.$route.params.server_id,
        pipeline_id: this.$route.params.pipeline_id
      }
      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/status?${searchParams}`
      fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          if (this.status !== data && data === 'run') {
            this.start()
          }
          this.status = data
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )

      if (this.$route.name == 'pipeline') {
        setTimeout(this.refetchStatus, 1000)
      }
    },
    onmousedown(event) {
      event.preventDefault()
      this.mouseDown = true
      this.$forceUpdate()
    },
    onmouseup(event) {
      event.preventDefault()
      this.mouseDown = false
      this.$forceUpdate()
    },
    move(event) {
      const imgElem = event.currentTarget.getElementsByTagName('img')
      if (!imgElem.length) {
        return
      }
      const img = imgElem[0]
      let transform = img.style.transform
      if ((transform, this.mouseDown)) {
        const translateRegex = /translate\((-)?[0-9]*px, (-)?[0-9]*px\)/g
        const translate = transform.match(translateRegex, '')
        let dx = 0
        let dy = 0
        let x = 0
        let y = 0
        if (translate) {
          const xy = translate[0].match(/-?[0-9]*px/g, '')
          x = parseFloat(xy[0])
          y = parseFloat(xy[1])
        }
        dx = x + event.movementX
        dy = y + event.movementY
        const scaleMatch = transform.match(/scale\([0-9]*.?[0-9]*\)/, '')
        if (scaleMatch && scaleMatch.length) {
          const scale = scaleMatch[0]
          img.style.transform = `${scale} translate(${dx}px, ${dy}px)`
        }
      }
    },
    zoom(event) {
      event.preventDefault()
      const imgElem = event.currentTarget.getElementsByTagName('img')
      if (!imgElem.length) {
        return
      }
      const img = imgElem[0]
      let scale = 1
      let translate = ''
      const transform = img.style.transform
      if (transform) {
        const scaleMatch = transform.match(/scale\([0-9]*.?[0-9]*\)/, '')
        if (scaleMatch && scaleMatch.length) {
          scale = parseFloat(scaleMatch[0].replace('scale(', ''))
        }
        const translateRegex = /translate\((-)?[0-9]*px, (-)?[0-9]*px\)/g
        const translateMatch = transform.match(translateRegex, '')
        if (translateMatch && translateMatch.length) {
          translate = translateMatch[0]
        }
      }
      scale += event.deltaY * -0.01
      if (scale < 1) {
        scale = 1
      }
      scale = Math.min(Math.max(0.125, scale), 8)
      img.style.transform = `scale(${scale}) ${translate}`
    },
    addNewElement(type) {
      if (type == 'processor') {
        this.isModalProcessor = true
      } else if (type == 'model') {
        this.isModalModel = true
      } else if (type == 'output') {
        this.isModalOutput = true
      }
    },
    toDuplicate() {
      this.$router.push({
        name: 'add_pipeline',
        query: {
          duplicate: this.$route.params.pipeline_id
        },
        params: {
          server_id: this.$route.params.server_id
        }
      })
    },
    duplicate() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/duplicate`
      this.serversLoading = true
      fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_id: this.$route.query.duplicate
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.configs = data.config
          this.advanceProperties = data.advance_properties

          this.availableProcessors = data.available_processors
          this.modelsNames = data.models_names
          this.outputsNames = data.outputs_names

          this.processorProperties = data.processor_properties
          this.outputProperties = data.output_properties
          this.modelProperties = data.model_properties

          this.setTitle(this.configs.name)
          this.setServerName(data.server_name)
          this.monitorLastActive()
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.serversLoading = false
        })
    },
    saveToFile() {
      const jsonData = JSON.stringify(this.configs)
      var a = document.createElement('a')
      var file = new Blob([jsonData], { type: 'application/json' })
      a.href = URL.createObjectURL(file)
      a.download = 'pipeline.json'
      a.click()
    },
    resizeWidget(event) {
      const dy = event.y - this.mousePosition
      this.mousePosition = event.y
      this.$refs['details'].style.height = `${parseInt(this.$refs['details'].style.height) + dy}px`
    },
    changeWidgetHeight(event) {
      this.mousePosition = event.y
      window.addEventListener('mousemove', this.resizeWidget)
      window.addEventListener('mouseup', this.stopChangeWidgetHeight)
    },
    stopChangeWidgetHeight() {
      window.removeEventListener('mousemove', this.resizeWidget)
      window.removeEventListener('mouseup', this.stopChangeWidgetHeight)
    },
    openChangeSourceModal() {
      const body = {
        server_id: this.$route.params.server_id
      }
      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT ||
        window.location.origin}/api/pipeline/source/available?${searchParams}`
      this.modalLoading = true
      return fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.sourcesNames = data
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.modalLoading = false
          this.isModalChangeSource = true
        })
    },
    gotoStart() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/source/goto-start`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          source_id: this.configs.source.source_id
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          this.$notify({
            message: 'Set pointer to the start',
            type: 'success'
          })
          return res.json()
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    changeSource(event) {
      const body = {
        server_id: this.$route.params.server_id,
        source_id: event.value
      }
      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/source?${searchParams}`
      this.modalLoading = true
      return fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.configs.source = data
          this.$forceUpdate()
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.modalLoading = false
          this.isModalChangeSource = false
        })
    },
    run(fullSpeed) {
      const el = this.$el.querySelector(':focus')
      if (el) el.blur()
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/run`
      this.image = null
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_config: this.configs,
          full_speed: fullSpeed
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.status = 'run'
          if (this.peerConnection == null) {
            this.start()
          } else {
            this.$refs.video.srcObject.getTracks()[0].enabled = true
          }
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    start() {
      const config = {
        sdpSemantics: 'unified-plan'
      }

      if (window.settings.video.stun === '') {
        this.peerConnection = new RTCPeerConnection()
      } else {
        config.iceServers = [{ urls: [window.settings.video.stun] }]
        this.peerConnection = new RTCPeerConnection(config)
      }

      // connect audio / video
      const vm = this
      this.peerConnection.addEventListener('track', function(evt) {
        if (evt.track.kind == 'video') {
          vm.$refs.video.srcObject = evt.streams[0]
        } else {
          vm.$refs.audio.srcObject = evt.streams[0]
        }
      })
      this.negotiate()
    },
    negotiate() {
      this.peerConnection.addTransceiver('video', { direction: 'recvonly' })
      this.peerConnection.addTransceiver('audio', { direction: 'recvonly' })
      return this.peerConnection
        .createOffer()
        .then(offer => {
          return this.peerConnection.setLocalDescription(offer)
        })
        .then(() => {
          // wait for ICE gathering to complete
          return new Promise(resolve => {
            if (this.peerConnection.iceGatheringState === 'complete') {
              resolve()
            } else {
              const checkState = () => {
                if (this.peerConnection.iceGatheringState === 'complete') {
                  this.peerConnection.removeEventListener('icegatheringstatechange', checkState)
                  resolve()
                }
              }
              this.peerConnection.addEventListener('icegatheringstatechange', checkState)
            }
          })
        })
        .then(() => {
          var offer = this.peerConnection.localDescription
          const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/video`
          return fetch(url, {
            credentials: 'include',
            body: JSON.stringify({
              sdp: offer.sdp,
              type: offer.type,
              server_id: this.$route.params.server_id,
              pipeline_id: this.$route.params.pipeline_id,
              size: 'f',
              watch_position: this.$refs.video.currentTime
            }),
            method: 'POST'
          })
        })
        .then(res => res.json())
        .then(answer => {
          return this.peerConnection.setRemoteDescription(answer)
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    stepForward() {
      const el = this.$el.querySelector(':focus')
      if (el) el.blur()
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/forward`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_config: this.configs
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.status = 'pause'
          this.getDetails()
          this.$refs.video.srcObject.getTracks()[0].enabled = false
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    stop() {
      const el = this.$el.querySelector(':focus')
      if (el) el.blur()
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/stop`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_config: this.configs
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.status = 'stop'
          this.peerConnection.close()
          this.peerConnection = null
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    addPipeline(event, type) {
      const body = {
        server_id: this.$route.params.server_id
      }
      if (type == 'processor') {
        body.processor_type = event.value
      } else if (type == 'model') {
        body.model_id = event.value
      } else if (type == 'output') {
        body.output_id = event.value
      }
      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/${type}?${searchParams}`
      this.modalLoading = true
      return fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.configs.pipeline.push({
            type,
            step_id: Math.floor(Math.random() * 10000000000),
            displayed_name: data.config.name,
            isNew: true,
            is_active: true,
            config: data.config,
            properties: data.properties,
            expand: true
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.modalLoading = false
          this.isModalProcessor = false
          this.isModalModel = false
          this.isModalOutput = false
        })
    },
    movedPipeline(event) {
      if (event.moved.oldIndex == this.monitoredPipeline) {
        this.monitoredPipeline = event.moved.newIndex
      } else if (event.moved.newIndex == this.monitoredPipeline) {
        if (this.monitoredPipeline > event.moved.oldIndex) {
          this.monitoredPipeline -= 1
        } else {
          this.monitoredPipeline += 1
        }
      }
      this.isRenderParametrs = false
      return this.$nextTick(() => (this.isRenderParametrs = true))
    },
    monitorLastActive() {
      this.monitoredPipeline = -1
      for (let i = this.configs.pipeline.length - 1; i >= 0; --i) {
        if (this.configs.pipeline[i].is_active && !this.configs.pipeline[i].isNew) {
          this.monitoredPipeline = i
          break
        }
      }
      this.getDetails()
    },
    changeMonitoredPipeline(key, activate = false, isActivate = false) {
      if (isActivate && activate) {
        let active_count = this.configs.pipeline.filter(el => el.is_active).length
        if (active_count == 1) {
          this.monitoredPipeline = key
          this.getDetails()
        }
      } else if (isActivate) {
        this.monitorLastActive()
      } else {
        this.monitoredPipeline = key
        this.getDetails()
      }
    },
    removePipeline(key) {
      if (Date.now() - this.isRemovePipelineClickedAt < 500) {
        return
      }
      this.isRemovePipelineClickedAt = Date.now()

      this.configs.pipeline.splice(key, 1)
      if (key == this.monitoredPipeline) {
        this.monitorLastActive()
      } else if (key < this.monitoredPipeline) {
        this.monitoredPipeline -= 1
      }
      this.$forceUpdate()
    },
    getImage() {
      const body = {
        server_id: this.$route.params.server_id,
        pipeline_id: this.$route.params.pipeline_id,
        watch_position: this.monitoredPipeline
      }
      const searchParams = new URLSearchParams(body)
      this.imageLoading = true
      const urlImage = `${process.env.VUE_APP_API_ENDPOINT ||
        window.location.origin}/api/pipeline/image?${searchParams}`
      return fetch(urlImage, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.image = data
          this.imageLoading = false
          if (this.isDrawingModalActive) {
            this.$nextTick(this.resize)
          }
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.imageLoading = false
          this.$forceUpdate()
        })
    },
    getDetails() {
      const body = {
        server_id: this.$route.params.server_id,
        pipeline_id: this.$route.params.pipeline_id,
        watch_position: this.monitoredPipeline
      }
      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/details?${searchParams}`
      this.detailsLoading = true
      fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.details = data
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.detailsLoading = false
          this.$forceUpdate()
        })

      // this.histogramLoading = true
      // const urlHistogram = `${process.env.VUE_APP_API_ENDPOINT ||
      //   window.location.origin}/api/pipeline/histogram?${searchParams}`
      // fetch(urlHistogram, {
      //   method: 'GET',
      //   credentials: 'include'
      // })
      //   .then(res => {
      //     if (!res.ok) {
      //       throw res.json()
      //     }
      //     return res.json()
      //   })
      //   .then(data => {
      //     this.histogram = data
      //   })
      //   .catch(error =>
      //     error.then(data => {
      //       this.$notify({
      //         message: JSON.parse(data.message).message,
      //         type: 'danger'
      //       })
      //     })
      //   )
      //   .finally(() => {
      //     this.histogramLoading = false
      //     this.$forceUpdate()
      //   })

      if (this.status === 'pause') {
        this.getImage()
      }
    },
    getDefaultConfig() {
      if (!this.serversLoading) {
        if (this.$route.name === 'add_pipeline' && this.$route.query.duplicate != undefined) {
          return this.duplicate()
        } else if (this.$route.name === 'add_pipeline') {
          this.$router.push({
            name: 'pipelines',
            params: {
              server_id: this.$route.params.server_id
            }
          })
        }
        const body = {
          server_id: this.$route.params.server_id
        }
        if (this.$route.query.type !== undefined) {
          body.type = this.$route.query.type
        }
        if (this.$route.params.pipeline_id !== undefined) {
          body.pipeline_id = this.$route.params.pipeline_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline?${searchParams}`
        this.serversLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.configs = data.config
            this.advanceProperties = data.advance_properties

            this.availableProcessors = data.available_processors
            this.modelsNames = data.models_names
            this.outputsNames = data.outputs_names

            this.processorProperties = data.processor_properties
            this.outputProperties = data.output_properties
            this.modelProperties = data.model_properties

            this.setTitle(this.configs.name)
            this.setServerName(data.server_name)
            this.monitorLastActive()
            this.refetchStatus()
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    save() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline`
      this.saving = true
      this.configs.name = this.title
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_config: this.configs
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$notify({
            message: this.$t('pipeline.saved'),
            type: 'success'
          })
          this.configs.pipeline.map(pipeline => (pipeline.isNew = false))
          if (this.$route.name === 'add_pipeline') {
            this.$router.push({
              name: 'pipelines',
              params: {
                server_id: this.$route.params.server_id
              }
            })
          }
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.saving = false
        })
    },
    saveAdvanced() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipeline/advanced`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_id: this.$route.params.pipeline_id,
          adv_parameters: this.configs.advanced_parameters
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$notify({
            message: this.$t('pipeline.advanced_saved'),
            type: 'success'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.serversLoading = false
        })
    },
    drawCanvas(item, key) {
      this.isDrawingModalActive = true
      this.shape = item.shape
      this.updateDrawItem = [key, item.key]
      try {
        this.coordinates = this.configs.pipeline[key].config.parameters[item.key]
          .replace(/\s/g, '')
          .replace(/\),/g, ') ')
          .split(' ')
          .map(s =>
            s
              .replace(/\)\(/g, ') (')
              .split(' ')
              .map(d => {
                const xy = d
                  .replace(/\(/g, '')
                  .replace(/\)/g, '')
                  .split(',')
                return {
                  x: Number(xy[0]),
                  y: Number(xy[1])
                }
              })
          )
          .flat()
      } catch {
        this.$notify({
          message: this.$t('pipeline.wrong_coordinates_format'),
          type: 'danger'
        })
      }
    },
    draw(item, key) {
      if (!this.image) {
        this.getImage().then(() => {
          this.drawCanvas(item, key)
        })
      } else {
        this.drawCanvas(item, key)
      }
    },
    drawShape(type) {
      this.active = true
      this.$emit('update:shape', type)
    },
    resize() {
      try {
        this.widthModal = this.$refs['visual-sample-modal'].clientWidth
        this.heightModal = this.$refs['visual-sample-modal'].clientHeight
      } catch (e) {
        this.widthModal = 100
        this.heightModal = 100
      }
    },
    getData({ coordinates, shape }) {
      if (this.updateDrawItem.length == 2) {
        this.configs.pipeline[this.updateDrawItem[0]].config.parameters[this.updateDrawItem[1]] = coordinates
          .map(point => `(${parseFloat(point.x).toFixed(2)}, ${parseFloat(point.y).toFixed(2)})`)
          .join(' ')
          .replace(/(\([+-]?\d+(\.\d+)?, [+-]?\d+(\.\d+)?\) \([+-]?\d+(\.\d+)?, [+-]?\d+(\.\d+)?\))./g, '$1, ')
      }
      this.coordinates = coordinates
      this.$emit('update:shape', shape)
      this.$emit('edit-data', coordinates)
    },
    editData({ shape, coordinates }) {
      this.coordinates = coordinates
      this.$emit('update:shape', shape)
      this.$emit('edit-data', this.coordinates)
    },
    closeDrawingModal() {
      if (this.status !== 'pause') {
        this.image = null
      }
      this.resize()
    },
    deletePipeline() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipelines/delete`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_ids: [this.$route.params.pipeline_id]
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$router.push({
            name: 'pipelines',
            params: {
              server_id: this.$route.params.server_id
            }
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    openDialogRemove() {
      this.$dialog.confirm({
        message: `${this.$t('confirm.remove', {
          msg: this.configs.name
        })}?`,
        onConfirm: () => {
          this.serversLoading = true
          this.deletePipeline()
            .then(() => {
              this.$notify({
                message: this.$t('notification.removed', {
                  msg: this.configs.name
                }),
                type: 'success'
              })
            })
            .finally(() => {
              this.serversLoading = false
            })
        }
      })
    }
  },
  mounted() {
    window.addEventListener('resize', this.resize)
    window.onbeforeunload = () => {
      if (this.peerConnection) {
        this.peerConnection.close()
        this.peerConnection = null
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }
    next()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    window.removeEventListener('icegatheringstatechange', this.resize)
    window.removeEventListener('track', this.resize)
  },
  destroyed() {
    window.removeEventListener('resize', this.resize)
  },
  watch: {
    coordinates: {
      immediate: true,
      handler(value) {
        this.coordinates = value
      }
    },
    isDrawingModalActive() {
      this.$nextTick(this.resize)
    }
  }
}
</script>

<style lang="scss">
.pipeline-settings {
  .visual-drawing {
    position: relative;
    width: 100%;
    &.zoomable {
      cursor: grab;
    }
    .visual-sample {
      object-fit: contain;
      max-height: 80vh;
    }
    .canvas-container {
      position: absolute !important;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: 0;
    }
  }
  .field.is-grouped.is-buttons-under-canvas {
    .control {
      margin-left: auto;
      margin-right: auto;
    }
  }
  .pipelines {
    float: left;
    width: 100%;
    margin-top: 1rem;
    margin-bottom: 1rem;
    &.advance {
      margin-top: 0;
      .pipeline {
        .header .name {
          cursor: unset;
          span {
            display: block;
            padding: 0.5rem;
            padding-top: 0.444rem;
            padding-bottom: 0.444rem;
          }
        }
        .body {
          .save {
            display: flex;
            margin-top: 1rem;
            .flex {
              flex: auto;
            }
            .button {
              font-size: 0.9rem;
              color: #4bbd4d;
              border-color: #4bbd4d;
              border-radius: 0.3125rem;
              font-weight: bold;
              padding-right: 0.625rem;
              padding-left: 0.625rem;
              height: 1.25rem;
            }
          }
        }
      }
    }
    .pipeline {
      margin-bottom: 0.8rem;
      &:last-child {
        margin: 0;
      }
      &.sortable-ghost {
        .header {
          .name {
            opacity: 0;
          }
          .actions {
            opacity: 0;
          }
        }
        .body {
          display: none;
        }
      }
      .header {
        border: 0.0625rem solid #ccc;
        background-color: #969696;

        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: space-between;

        &.processor {
          background-color: #59adff;
        }
        &.model {
          background-color: #fc4850;
        }
        &.output {
          background-color: #b032e7;
        }
        &.inactive {
          background-color: #d0d0d0;
          color: #777;
        }

        .name {
          cursor: pointer;
          flex: auto;
          .title {
            font-size: 0.9rem;
            padding-top: 0.444rem;
            padding-bottom: 0.444rem;
            height: 100%;
            display: block;
          }
          i.handle {
            padding: 0.5rem;
            cursor: grab;
            display: block;
            float: left;
            height: 100%;
          }
        }
        .actions {
          display: flex;
          .switch {
            display: flex;
            align-items: unset;
            padding-top: 0.4rem;
            .control-label {
              display: none;
            }
            input {
              display: none;
            }
          }
          i {
            cursor: pointer;
            padding: 0.5rem;
            &.unmonitored {
              opacity: 0.2;
            }
          }
        }
      }
      .body {
        padding: 1rem;
        border: 0.0625rem solid #ccc;
        border-top: 0;
        .description {
          margin-bottom: 1rem;
        }
      }
    }
  }
  .add-new {
    button.add {
      .text {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
      }
    }
    .dropdown-menu {
      width: 100%;
    }
  }
  .actions {
    float: right;
  }
  .pipeline-details {
    float: left;
    width: 100%;
    margin-top: 1rem;
    .video {
      border: 0.0625rem solid #ccc;
      display: flex;
      margin-bottom: 0.8rem;
      overflow: hidden;
      .video-data {
        display: flex;
        width: 100%;
        max-height: 70vh;
        #video {
          width: 100%;
        }
      }
      image {
        cursor: grab;
      }
    }
    .histogram {
      border: 0.0625rem solid #ccc;
      display: flex;
      margin-bottom: 0.8rem;
      overflow: hidden;
      cursor: grab;
    }
    .server-info {
      padding: 1rem;
      border: 0.0625rem solid #ccc;
      display: flex;
      margin-bottom: 0.8rem;
      &.widget {
        padding: 0;
        position: relative;
        display: grid;
        .details {
          padding: 1rem;
          width: 100%;
          margin-bottom: 1rem;
          overflow: auto;
        }
        i {
          width: 1.5rem;
          height: 1.5rem;
          position: absolute;
          bottom: 0;
          right: 0;
          cursor: ns-resize;
        }
      }
      .description {
        flex: auto;
        .source-name {
          height: 2rem;
          .name {
            color: #62b1ff;
            font-weight: bold;
          }
        }
        .button {
          font-size: 0.9rem;
          color: #4bbd4d;
          border-color: #4bbd4d;
          border-radius: 0.3125rem;
          font-weight: bold;
          padding-right: 0.625rem;
          padding-left: 0.625rem;
          height: 1.25rem;
        }
      }
      .actions {
        .buttons {
          margin: 0;
          .button {
            border: 0;
            padding: 0;
            margin-left: 0.5rem;
            height: max-content;
            color: #686868;
            background-color: #fff;
            font-size: 1.5rem;
            &.forward {
              .forward {
                display: contents;
              }
            }
            &.play {
              &.active {
                color: #4bbd4d;
              }
            }
            &.stop {
              &.active {
                color: #fc4850;
              }
            }
          }
          .indicator {
            width: 100%;
            display: block;
            text-align: center;
          }
        }
        .status {
          width: 100%;
          text-align: center;
          display: block;
        }
      }
    }
  }
}
</style>
